import { useUserBoostedFarmsInfo } from 'state/farmBoost/useUserBoostedFarmsInfos'
import {
  getRewarderV2AddressByPid,
  useArgoNftIsApprovalForAll,
  useViewUserInfo as useViewUserInfoInRewarderV2,
} from 'hooks/rewarderV2/useRewarderV2Contract'
import { useBalanceOfUser, useIsApprovalForAll } from 'hooks/vvs-miner-mole/useMoleContract'
import { getVvsFarmBoostAddress } from 'utils/addressHelpers'
import { isFarmBoostingSupported, isEcoFarmSupported } from 'utils/isNftStakingSupported'
import { useGetEcoFarmNftCollections } from 'views/Farms/hooks/useGetEcoFarmNftCollections'

interface StakeInfo {
  isLoading: boolean
  isApprovedForAll: boolean
  nftBalanceOfUser: number
  boostingNfts: string[]
}
const useGetNftStakeInfo = (farmId: number): StakeInfo => {
  const isFarmBoost = isFarmBoostingSupported(farmId)
  const isEcoFarm = isEcoFarmSupported(farmId)

  // Miner Mole
  const farmBoostStakeInfo = useGetFarmBoostStakeInfo(farmId, isFarmBoost)

  // ECO farm
  const ecoFarmStakeInfo = useGetEcoFarmStakeInfo(farmId, isEcoFarm)

  let initInfoData = {
    isLoading: true,
    isApprovedForAll: false,
    nftBalanceOfUser: 0,
    boostingNfts: [],
  }

  if (isFarmBoost) {
    initInfoData = {
      ...initInfoData,
      ...farmBoostStakeInfo,
    }
  }

  if (isEcoFarm) {
    initInfoData = {
      ...initInfoData,
      ...ecoFarmStakeInfo,
    }
  }

  return initInfoData
}

const useGetFarmBoostStakeInfo = (farmId: number, enabled: boolean): StakeInfo => {
  // Miner Mole
  const { data: isFarmBoostApprovedForAll, isLoading: isFarmBoostApprovedForAllLoading } = useIsApprovalForAll(
    getVvsFarmBoostAddress(),
    {
      enabled,
    },
  )
  const { data: farmBootNftBalanceOfUser, isLoading: isLoadingFarmBootBalanceOfUser } = useBalanceOfUser({
    enabled,
  })
  const [farmBoostUserInfo, isGetFarmUserInfoLoaded] = useUserBoostedFarmsInfo(farmId)
  const farmBoostBoostingNfts = farmBoostUserInfo?.boostingNfts ?? []

  return {
    isLoading: isFarmBoostApprovedForAllLoading || isLoadingFarmBootBalanceOfUser || !isGetFarmUserInfoLoaded,
    isApprovedForAll: isFarmBoostApprovedForAll,
    nftBalanceOfUser: farmBootNftBalanceOfUser,
    boostingNfts: farmBoostBoostingNfts,
  }
}

const useGetEcoFarmStakeInfo = (farmId: number, enabled: boolean): StakeInfo => {
  const rewarderV2AddressOfCurrentFarm = getRewarderV2AddressByPid(farmId)

  const { data: isArgoNftApprovedForAll, isLoading: isArgoNftApprovedForAllLoading } = useArgoNftIsApprovalForAll(
    rewarderV2AddressOfCurrentFarm,
    {
      enabled, // only supported eco boost farm need to call this ABI, [farm.ecoFarm != false / undefined]
    },
  )

  const { data: userNftCollections, isLoading: isUserNftCollectionsLoading } = useGetEcoFarmNftCollections(farmId)

  const { data: viewUserRewarderV2InfoData, isLoading: isViewUserRewarderV2InfoLoading } = useViewUserInfoInRewarderV2(
    rewarderV2AddressOfCurrentFarm,
    farmId,
    {
      enabled, // only supported eco boost farm need to call this ABI, [farm.ecoFarm != false / undefined]
    },
  )
  const argoBoostingNfts = viewUserRewarderV2InfoData ? (viewUserRewarderV2InfoData[2] as []) : []

  return {
    isLoading: isViewUserRewarderV2InfoLoading || isArgoNftApprovedForAllLoading || isUserNftCollectionsLoading,
    isApprovedForAll: isArgoNftApprovedForAll,
    nftBalanceOfUser: userNftCollections?.totalCount,
    boostingNfts: argoBoostingNfts as [],
  }
}

export default useGetNftStakeInfo
